/**
 * Ordenar as listas do Painel de Atendimentos por coluna.
 *
 * Pedido do dono do produto em 14/10: cabeçalho clicável, para achar quem
 * espera há mais tempo, ou um cliente pelo nome, sem ler a lista inteira.
 *
 * ## Por que um módulo, e não um `sort` em cada cartão
 *
 * São três listas com dados diferentes (agentes, fila, em atendimento) e as
 * mesmas quatro armadilhas em todas:
 *
 * - **texto com acento**: um `>` cru manda "Ângela" para depois de "Zuleica",
 *   porque compara código de caractere. Num painel de clientes brasileiros
 *   isso quebra a ordem alfabética na cara de quem olha.
 * - **número como texto**: `'10' < '9'` é verdadeiro. A coluna "Qtd. Atend."
 *   mostraria 10 antes de 9.
 * - **vazio**: operador sem atendimento, sala sem empresa. Tratar `null` como
 *   zero ou como string vazia mistura "não tem" com "tem nada", e muda de
 *   lugar conforme a direção.
 * - **empate**: o painel se redesenha a cada atualização. Ordenação instável
 *   faria linhas empatadas trocarem de lugar sozinhas, e a tela pareceria
 *   viva sem nada ter mudado.
 *
 * Resolvido uma vez, com teste, em vez de três vezes na mão.
 */

export const CRESCENTE = 'asc';
export const DECRESCENTE = 'desc';

/**
 * Como comparar cada tipo de coluna.
 *
 * `texto` usa `localeCompare` em pt-BR com `sensitivity: 'base'`, que iguala
 * acento e caixa -- é a ordem que uma pessoa espera ver, não a da tabela
 * Unicode.
 */
const COMPARADORES = {
    texto: (a, b) => String(a).localeCompare(String(b), 'pt-BR', { sensitivity: 'base' }),
    numero: (a, b) => Number(a) - Number(b),
    data: (a, b) => new Date(a).getTime() - new Date(b).getTime(),
};

/** Vazio é o que não dá para comparar: nulo, indefinido ou string em branco. */
const estaVazio = (v) => v === null || v === undefined || (typeof v === 'string' && v.trim() === '');

/**
 * O próximo estado ao clicar num cabeçalho.
 *
 * Clicar numa coluna NOVA começa crescente; clicar na mesma inverte; o
 * terceiro clique **desliga** a ordenação e devolve a lista à ordem do
 * servidor.
 *
 * O terceiro clique existe porque a ordem natural carrega informação que
 * nenhuma coluna carrega -- a fila vem do servidor na ordem de chegada. Sem
 * desligar, quem ordenou por nome uma vez não teria como voltar a ver a fila
 * como fila sem recarregar a página.
 */
export const proximaOrdem = (atual, chave) => {
    if (!atual || atual.chave !== chave) return { chave, direcao: CRESCENTE };
    if (atual.direcao === CRESCENTE) return { chave, direcao: DECRESCENTE };
    return null;
};

/**
 * A lista ordenada.
 *
 * @param {Array} lista
 * @param {{chave: string, direcao: string}|null} ordem  nulo devolve a lista
 *        como veio -- é o terceiro clique.
 * @param {Object} colunas  mapa `chave -> { tipo, valor(item) }`.
 */
export const ordenarPor = (lista, ordem, colunas) => {
    const itens = Array.isArray(lista) ? lista : [];

    if (!ordem || !ordem.chave) return itens;

    const coluna = colunas?.[ordem.chave];

    // Coluna desconhecida devolve a lista intacta em vez de ordenar por algo
    // inventado: errar a ordem em silêncio é pior que não ordenar.
    if (!coluna || typeof coluna.valor !== 'function') return itens;

    const comparar = COMPARADORES[coluna.tipo] || COMPARADORES.texto;
    const sinal = ordem.direcao === DECRESCENTE ? -1 : 1;

    // Cópia: `sort` ordena no lugar, e a lista vem do estado do React --
    // mutá-la é mudar o estado sem avisar o React.
    return [...itens].sort((a, b) => {
        const va = coluna.valor(a);
        const vb = coluna.valor(b);

        // Vazio vai para o FIM nas duas direções, e não "no começo quando
        // desce". Quem clica procura o maior ou o menor valor REAL; uma fileira
        // de brancos no topo só esconde o que a pessoa foi buscar.
        if (estaVazio(va) && estaVazio(vb)) return 0;
        if (estaVazio(va)) return 1;
        if (estaVazio(vb)) return -1;

        return sinal * comparar(va, vb);
    });
};

/** O rótulo de acessibilidade do cabeçalho, dizendo o que o clique VAI fazer. */
export const rotuloDaOrdenacao = (titulo, ordem, chave) => {
    if (!ordem || ordem.chave !== chave) return `Ordenar por ${titulo}`;
    if (ordem.direcao === CRESCENTE) return `Ordenar por ${titulo} (decrescente)`;
    return `Remover ordenação de ${titulo}`;
};
